import { motion } from 'framer-motion';
import { Database, ArrowRight, Building2, FileCheck, Shield } from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';

const orgData = {
  'Org ID': 'ORG-7781',
  'Org Name': 'Al Noor Healthcare Group',
  'Owner EID': '784-1234-5678901-1',
  'Owner Name': 'Ahmed Al Rashid',
};

const facilityData = {
  'Facility Name': 'Al Noor Medical Center',
  'Facility License': 'LIC-2024-00892',
  'Facility Type': 'Hospital',
  'Facility Sub Type': 'General Hospital',
  'Address': 'Sheikh Zayed Road, Dubai, UAE',
};

const payerData = {
  'Payer ID': 'PAY-4420',
  'Payer Name': 'ADNIC',
  'Profile Type': 'Insurance Company',
};

const payerFacilityData = {
  'Facility Name': 'ADNIC Claims Center',
  'Facility License': 'LIC-2024-03201',
  'Facility Type': 'Insurance',
  'Facility Sub Type': 'Claims Processing',
  'Address': 'Hamdan Street, Abu Dhabi, UAE',
};

function DataCard({ title, data, icon: Icon, delay, color }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white rounded-2xl border border-black/5 shadow-xl p-5 w-full"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
        <h3 className="font-heading font-bold text-[15px] text-[#0A0A0A]">{title}</h3>
      </div>
      <div className="space-y-2">
        {Object.entries(data).map(([key, value], i) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: delay + 0.3 + i * 0.1 }}
            className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-[#FAFAFA]"
          >
            <span className="text-[13px] font-body text-[#737373]">{key}</span>
            <span className="text-[13px] font-body font-medium text-[#0A0A0A]">{value}</span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

export default function AcellaDataScene() {
  return (
    <div data-testid="scene-acella-data" className="flex flex-col items-center justify-center h-full px-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-6xl"
      >
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-medium bg-teal-600/10 text-teal-700 mb-4">
            <Database size={14} /> Step 1
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-[#0A0A0A]">
            Receive Data from Acella
          </h2>
          <p className="mt-3 font-body text-lg text-[#737373] flex items-center justify-center gap-2">
            Organization, facility and payer details are synced automatically
            <InfoSpot text="Acella is the external system that sends organization, facility, and payer data to Shafafiya. This sync happens automatically — Shafafiya receives and processes the data to build profiles." position="bottom" />
          </p>
        </motion.div>

        {/* Data flow visualization */}
        <div className="flex items-center justify-center gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="px-6 py-3 rounded-2xl bg-[#0A0A0A] text-white font-heading font-bold text-lg"
          >
            Acella
          </motion.div>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.6, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-2 origin-left"
          >
            <div className="w-32 h-[2px] bg-gradient-to-r from-[#0A0A0A] to-teal-600" />
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
              <ArrowRight size={20} className="text-teal-600" />
            </motion.div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="px-6 py-3 rounded-2xl bg-teal-600 text-white font-heading font-bold text-lg"
          >
            Shafafiya
          </motion.div>
        </div>

        {/* Provider: Org + Facility */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
          <p className="text-xs font-body font-medium text-[#737373] uppercase tracking-wider mb-3 ml-1">Healthcare Provider</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <DataCard title="Organization Details" data={orgData} icon={Building2} delay={0.7} color="bg-[#0A0A0A]" />
            <DataCard title="Facility Details" data={facilityData} icon={FileCheck} delay={0.9} color="bg-teal-600" />
          </div>
        </motion.div>

        {/* Payer: Payer + Facility (no org) */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>
          <p className="text-xs font-body font-medium text-[#737373] uppercase tracking-wider mb-3 ml-1">Payer / Insurance <span className="normal-case text-[#737373]/60">(facility only, no organization)</span></p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <DataCard title="Payer Details" data={payerData} icon={Shield} delay={1.3} color="bg-amber-600" />
            <DataCard title="Facility Details" data={payerFacilityData} icon={FileCheck} delay={1.5} color="bg-amber-500" />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
